import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Radio,
  Pagination,
  Modal,
  ThemeProvider,
  createTheme,
  IconButton,
  CircularProgress,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "@fontsource/manrope";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
  },
});

const TestRailProjectsComponentPage = ({
  issueType,
  taskType,
  projectsLoading,
  setOpenProjectPage,
  projectsList,
  selectedProject,
  setSelectedProject,
  onProjectSelected,
  setOpenTemplatePage,
  setSelectedTemplate,
  setTemplates,
  setSelectedSuite,
  setSuitesList,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(true);

  const projectsPerPage = 6;
  const handleRadioChange = (project) => {
    setSelectedProject(project);
    setSelectedTemplate(null); // reset template
    setTemplates([]); // clear templates list
    setSelectedSuite(null); // reset suite
    setSuitesList([]); // clear suites list
    setOpenTemplatePage(true); // open template modal
    if (onProjectSelected) onProjectSelected(project);
  };

  const handlePageChange = (event, value) => setPage(value);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setOpenProjectPage(false);
  };

  const filteredProjects = projectsList.filter((project) =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedProjects = filteredProjects.slice(
    (page - 1) * projectsPerPage,
    page * projectsPerPage
  );

  return (
    <ThemeProvider theme={theme}>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            height: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
            display: "flex",
            flexDirection: "column",
            outline: "none",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseModal}
            sx={{ position: "absolute", top: 8, right: 8 }}
            aria-label="Close project selection"
          >
            <CloseIcon />
          </IconButton>

          {/* Modal Header */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            {taskType} {issueType}
          </Typography>

          {/* Search + Instruction */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Select a project to move forward
            </Typography>
            <TextField
              size="small"
              placeholder="Search projects"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              sx={{ width: 200 }}
            />
          </Box>

          {/* Main Content */}
          {projectsList.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {projectsLoading ? (
                <CircularProgress />
              ) : (
                <Typography>No projects found</Typography>
              )}
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1.5,
                  flex: 1,
                  overflowY: "auto",
                }}
              >
                {displayedProjects.map((project) => (
                  <Card
                    key={project.id}
                    variant="outlined"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: 90,
                      p: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                    }}
                    onClick={() => handleRadioChange(project)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", ml: -1 }}>
                      <Radio
                        checked={selectedProject?.id === project.id}
                        value={project.id}
                        name="testrail-project-radio"
                        onChange={() => handleRadioChange(project)}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {project.name}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </Box>

              {/* Pagination */}
              <Pagination
                count={Math.ceil(filteredProjects.length / projectsPerPage)}
                page={page}
                onChange={handlePageChange}
                sx={{ mt: 2, display: "flex", justifyContent: "center" }}
              />
            </>
          )}
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default TestRailProjectsComponentPage;
